import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, CheckCircle2, XCircle, RefreshCw, GitBranch, GitPullRequest,
  Database, ShieldCheck, Cpu, Clock, Copy, ArrowLeft, Terminal
} from "lucide-react";
import { toast } from "sonner";
import { WavygoLogo } from "@/components/WavygoLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

export default function CICDStatus() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [latency, setLatency] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [copied, setCopied] = useState(false);

  const checkStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    const start = performance.now();
    try {
      const res = await api.get("/ci-cd-status");
      const duration = Math.round(performance.now() - start);
      setLatency(duration);
      setData(res.data);
      setLastChecked(new Date().toLocaleTimeString());
    } catch (err) {
      const duration = Math.round(performance.now() - start);
      setLatency(duration);
      setError(err?.response?.data?.detail || err.message || "Failed to connect to backend");
      setLastChecked(new Date().toLocaleTimeString());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const copyDiagnostics = () => {
    const report = {
      timestamp: new Date().toISOString(),
      latency_ms: latency,
      status: error ? "failed" : data?.status || "unknown",
      backend_response: data || error,
      client_user_agent: navigator.userAgent,
    };
    navigator.clipboard.writeText(JSON.stringify(report, null, 2));
    setCopied(true);
    toast.success("Diagnostic report copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const isHealthy = !error && data?.status === "healthy";

  return (
    <div className="min-h-screen bg-background text-foreground py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* Top bar navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-xs text-muted-foreground hover:underline">
              Sign In
            </Link>
          </div>
        </div>

        {/* Hero banner */}
        <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/[0.08] via-card to-background p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-start sm:items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-white/95 dark:bg-card border border-border flex items-center justify-center shadow-md shrink-0">
                <WavygoLogo forceVariant="green" className="h-8" />
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
                    CI/CD Pipeline Status
                  </h1>
                  <Badge variant={isHealthy ? "default" : "destructive"} className="gap-1.5 py-1 px-2.5 font-medium">
                    <span className={`h-2 w-2 rounded-full ${isHealthy ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
                    {loading ? "Checking..." : isHealthy ? "Pipeline Active" : "Degraded"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                  Automated upstream verification monitor for GitHub Actions CI/CD workflows, backend API health, and runtime services.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={checkStatus}
                disabled={loading}
                className="gap-2 flex-1 sm:flex-none"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                <span>Run Health Check</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={copyDiagnostics}
                className="gap-2 flex-1 sm:flex-none"
              >
                <Copy className="h-3.5 w-3.5" />
                <span>{copied ? "Copied!" : "Copy Report"}</span>
              </Button>
            </div>
          </div>

          {lastChecked && (
            <div className="mt-4 pt-4 border-t border-border/50 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" />
                Last pinged: <span className="font-medium text-foreground">{lastChecked}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5 text-emerald-500" />
                Latency: <span className="font-medium text-foreground">{latency !== null ? `${latency} ms` : "..."}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <GitBranch className="h-3.5 w-3.5 text-sky-500" />
                Upstream Repo: <span className="font-mono text-foreground">theanilanand/app</span>
              </span>
            </div>
          )}
        </div>

        {/* 4 Overview Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold">Backend API</CardDescription>
              <CardTitle className="text-xl flex items-center justify-between">
                <span>{error ? "Offline" : "Operational"}</span>
                {error ? (
                  <XCircle className="h-5 w-5 text-destructive" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Endpoint: <code className="text-foreground">/api/ci-cd-status</code>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold">Database (MongoDB)</CardDescription>
              <CardTitle className="text-xl flex items-center justify-between">
                <span className="capitalize">{data?.system?.database_status || "Checking"}</span>
                <Database className="h-5 w-5 text-primary" />
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Target: <code className="text-foreground">{data?.system?.database_name || "wavygo_db"}</code>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold">CI Runner</CardDescription>
              <CardTitle className="text-xl flex items-center justify-between">
                <span>Ubuntu 24.04</span>
                <Cpu className="h-5 w-5 text-sky-500" />
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Python 3.12 · Node.js 20
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold">Target Branches</CardDescription>
              <CardTitle className="text-xl flex items-center justify-between">
                <span>garv-agarwal, main</span>
                <GitPullRequest className="h-5 w-5 text-amber-500" />
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Triggers on Push & Pull Request
            </CardContent>
          </Card>
        </div>

        {/* Detailed Pipeline Breakdown */}
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Upstream GitHub Actions Pipeline Breakdown</CardTitle>
            </div>
            <CardDescription>
              The workflow configuration in <code className="text-foreground">.github/workflows/ci.yml</code> validates code on every push to upstream.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="rounded-xl border border-border p-4 bg-muted/20 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Backend Tests & Quality Checks</h3>
                    <p className="text-xs text-muted-foreground">Runs isolated Pytest suite with live MongoDB 7 service</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                  Passing in CI
                </Badge>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground pl-11">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  MongoDB 7 container health probe
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  Python 3.12 dependencies (requirements.txt)
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  CI environment setup (ci_setup.py)
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  Full test suite (pytest -n 2 --dist loadscope)
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-border p-4 bg-muted/20 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-sky-500/10 text-sky-500 flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Frontend Build Verification</h3>
                    <p className="text-xs text-muted-foreground">Compiles React 19 application bundle</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-sky-600 dark:text-sky-400 border-sky-500/30">
                  Passing in CI
                </Badge>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground pl-11">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-sky-500" />
                  Node.js 20 environment initialization
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-sky-500" />
                  Clean dependency install (npm ci --legacy-peer-deps)
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-sky-500" />
                  Production CRA/Craco bundle compilation
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-sky-500" />
                  Zero build error assertion (CI=false npm run build)
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-border p-4 bg-muted/20 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Continuous Deployment & Health Verification</h3>
                    <p className="text-xs text-muted-foreground">Live verification of deployed API and UI endpoints</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-amber-600 dark:text-amber-400 border-amber-500/30">
                  Live Active
                </Badge>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground pl-11">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-amber-500" />
                  Real-time live heartbeat probe (/api/health)
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-amber-500" />
                  Live verification diagnostics (/api/ci-cd-status)
                </li>
              </ul>
            </div>

          </CardContent>
        </Card>

        {/* Live Diagnostics Output */}
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Terminal className="h-4 w-4 text-primary" />
                Live API Response Diagnostics
              </CardTitle>
              <CardDescription>Direct JSON payload returned by backend endpoint</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={copyDiagnostics} className="h-8 gap-1.5 text-xs">
              <Copy className="h-3 w-3" />
              <span>Copy JSON</span>
            </Button>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4 text-xs font-mono text-destructive">
                Error connecting to backend: {String(error)}
              </div>
            ) : (
              <pre className="rounded-lg bg-slate-950 text-slate-100 p-4 text-xs font-mono overflow-x-auto max-h-[300px] scrollbar-thin">
                {data ? JSON.stringify(data, null, 2) : "Fetching status..."}
              </pre>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
